
import React, { useState, forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, LucideIcon } from 'lucide-react';
import { FormLabel } from '@/components/ui/form';

interface IconInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon: LucideIcon;
  showPasswordToggle?: boolean;
  passwordVisible?: boolean;
  onTogglePassword?: () => void;
  label?: string;
  field?: any;
}

const IconInput = forwardRef<HTMLInputElement, IconInputProps>(({
  icon: Icon,
  showPasswordToggle,
  passwordVisible: externalPasswordVisible,
  onTogglePassword: externalTogglePassword,
  label,
  field,
  type,
  placeholder,
  ...props
}, ref) => {
  const [internalPasswordVisible, setInternalPasswordVisible] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const passwordVisible = externalPasswordVisible !== undefined ? externalPasswordVisible : internalPasswordVisible;
  
  const handleTogglePassword = () => {
    if (externalTogglePassword) {
      externalTogglePassword();
    } else {
      setInternalPasswordVisible(!internalPasswordVisible);
    }
  };
  
  // Determine the input type based on the passwordVisible state
  const inputType = type === 'password' && passwordVisible ? 'text' : type;
  
  return (
    <div className="space-y-2">
      {label && <FormLabel>{label}</FormLabel>}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <Input
          className="pl-10 pr-10 border border-gray-300 focus:border-primary-blue focus:ring-1 focus:ring-primary-blue"
          type={inputType}
          ref={ref}
          placeholder={placeholder}
          {...field}
          {...props}
        />
        {type === 'password' && showPasswordToggle && (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
            onClick={handleTogglePassword}
            aria-label={passwordVisible ? "Hide password" : "Show password"}
          >
            {passwordVisible ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

IconInput.displayName = 'IconInput';

export default IconInput;
